import React, { useCallback } from 'react';
import { Field as FinalField, useForm } from 'react-final-form';
import { get } from 'lodash';
import { AutoComplete, DataSourceItem } from '@lendi-ui/auto-complete';
import getColour from '@lendi-ui/commons/colours';
import Field from '@lendi-ui/field';
import Search from '@lendi-ui/icon/Search';

import { useUpdatedRef } from '@funnels/hooks';
import { validateNotUndefined } from '@funnels/utils';

import { FieldLabel } from '../_common/FieldLabel';
import { FieldWrapper, Prefix } from '../_common/style';
import { CommonFieldProps } from '../_common/types';

export interface AutoCompleteInputFieldProps extends CommonFieldProps {
  dataSource: ((input: string) => Promise<DataSourceItem[]>) | DataSourceItem[];
  handleSelectedItem?: (value: DataSourceItem) => void;
  isFullWidth?: boolean;
  placeholder?: string;
  setValueOnUserTyping?: boolean;
  isDisabled?: boolean;
  backupOption?: DataSourceItem;
  menuContainerRef?: React.RefObject<HTMLUListElement>;
}

const SearchIcon = () => (
  <Prefix>
    <Search color={getColour('text', 'subtle')} height="24px" width="24px" />
  </Prefix>
);

const AutoCompleteInputField = (props: AutoCompleteInputFieldProps) => {
  const {
    disableFailMessage = false,
    handleSelectedItem,
    isOptional,
    label,
    labelAfter,
    name,
    setValueOnUserTyping = false,
    subLabel,
    validate: validateFn,
    validationFailMessage,
    ...otherProps
  } = props;
  const form = useForm();
  const formValues = form.getState().values;

  const validationRef = useUpdatedRef({ isOptional });

  const validate = (value: any) => {
    if (validateFn) {
      return validateFn(value);
    } else {
      return validateNotUndefined(validationFailMessage, validationRef.current.isOptional)(value);
    }
  };

  const fieldProps = {
    name,
    validate,
  };

  const onSelectItem = useCallback((selected: DataSourceItem) => {
    if (selected.value === '') {
      return form.change(name, undefined);
    }
    form.change(name, selected.value);

    if (handleSelectedItem) {
      handleSelectedItem(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (setValueOnUserTyping) {
      form.change(name, event.target.value);
    } else {
      form.change(name, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FieldWrapper>
      <FieldLabel label={label} labelAfter={labelAfter} subLabel={subLabel} isOptional={isOptional} />
      <FinalField {...fieldProps}>
        {({ input, meta }) => (
          <Field {...meta} error={disableFailMessage ? undefined : meta.error}>
            <AutoComplete
              data-component={name}
              size="md"
              before={<SearchIcon />}
              onSelectItem={onSelectItem}
              {...otherProps}
              initialValue={get(formValues, name)}
              isError={!!(meta.error && meta.touched)}
              onChange={onChange}
            />
          </Field>
        )}
      </FinalField>
    </FieldWrapper>
  );
};

export default AutoCompleteInputField;
